import React, { useState, useEffect } from 'react';
import { getImageUrl } from '@/lib/utils/image';

export interface TarotCardData {
  title?: string;
  description?: string;
  imageUrl?: string;
  metadata: {
    [key: string]: any;
    language: string;
    playerName: string;
    suit?: string;
    number?: string;
    keywords?: string[];
    elementalSymbol?: string;
    isReversed?: boolean;
  };
}

interface TarotCardProps {
  cardData: TarotCardData;
}

export const TarotCard: React.FC<TarotCardProps> = ({ cardData }) => {
  // console.log('TarotLayout cardData:', cardData);
  const [imageError, setImageError] = useState(false);
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Image load error for URL:', imageUrl);
    setImageError(true);
    e.currentTarget.onerror = null;
  };

  const toRoman = (num: number): string => {
    if (num === 0) return "0";
    const roman = {
      M: 1000, CM: 900, D: 500, CD: 400,
      C: 100, XC: 90, L: 50, XL: 40,
      X: 10, IX: 9, V: 5, IV: 4, I: 1
    };
    let str = '';
    for (let i of Object.keys(roman)) {
      let q = Math.floor(num / roman[i as keyof typeof roman]);
      num -= q * roman[i as keyof typeof roman];
      str += i.repeat(q);
    }
    return str;
  };

  const suit = cardData.metadata.suit || 'major';
  const number = cardData.metadata.number || '0';
  const isReversed = cardData.metadata.isReversed || false;
  const keywords = cardData.metadata.keywords || [];
  // const elementalSymbol = cardData.metadata.elementalSymbol || 'Air';

  // console.log('Image URL debug:', {
  //   original: cardData.imageUrl,
  //   processed: cardData.imageUrl 
  //     ? cardData.imageUrl.startsWith('http')
  //       ? cardData.imageUrl.replace('/api/api/', '/api/')
  //       : `${import.meta.env.VITE_API_URL}${cardData.imageUrl}`
  //     : "/placeholder.jpg",
  //   apiUrl: import.meta.env.VITE_API_URL
  // });

  const imageUrl = getImageUrl(cardData.imageUrl);
  // console.log('TarotLayout imageUrl:', imageUrl, 'VITE_API_URL:', import.meta.env.VITE_API_URL);
  // console.log('TarotLayout cardData:', cardData);

  useEffect(() => {
    console.log('TarotLayout received cardData:', cardData);
    console.log('Processed imageUrl:', imageUrl);
  }, [cardData, imageUrl]);

  return (
    <div className={`w-80 min-h-[500px] relative overflow-hidden ${isReversed ? 'rotate-180' : ''}`}>
      {/* Main card container with vintage paper texture */}
      <div className="w-full h-full relative overflow-hidden rounded-lg">
        {/* Base vintage paper background */}
        <div className="absolute inset-0 bg-stone-50 opacity-90 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-stone-100/30 to-stone-200/30" />
        </div>
        
        {/* Example with different texture image approaches */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200/50 via-white/70 to-gray-200/50 mix-blend-overlay z-20" />

        <div className="absolute inset-0 mix-blend-soft-light opacity-40 z-20">
          <div className="w-full h-full bg-gradient-to-br from-neutral-200 to-neutral-300 bg-repeat" />
        </div>

        <div className="absolute inset-0 mix-blend-color-burn opacity-30 z-30">
          <div className="w-full h-full backdrop-blur-sm backdrop-contrast-125" />
        </div>

        <div className="absolute inset-0 mix-blend-color-dodge opacity-20 z-40">
          <div className="w-full h-full bg-gradient-to-tr from-white/30 to-transparent" />
        </div>

        {/* Option 2: Using a minimal base64 (for simple patterns) */}
        <div className="absolute inset-0 mix-blend-soft-light opacity-30 z-10">
          <div className="w-full h-full bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAEklEQVQImWNgYGD4z0AswK4SAFXuAf8EPy+xAAAAAElFTkSuQmCC')] bg-repeat" />
        </div>

        {/* Option 3: Using gradients (no image needed) */}
        <div className="absolute inset-0 mix-blend-multiply opacity-10 z-10">
          <div className="w-full h-full bg-gradient-radial from-transparent via-neutral-800/10 to-transparent" />
        </div>
        
        {/* Additional texture layer */}
        {/* Card content container */}
        <div className="relative h-full flex flex-col items-center p-6 z-20">
          
          {/* Top decorative flourish */}
          <div className="w-full h-6 mb-4 opacity-0" aria-hidden="true" />

          {/* Card number in vintage style */}
          <div className="mb-4">
            <span className="font-serif text-2xl text-neutral-800 tracking-widest">
              {suit === 'major' ? toRoman(parseInt(number)) : number}
            </span>
          </div>

          {/* Main image container with decorative frame */}
          <div className="relative w-full flex-grow mb-6">
            <div className="absolute inset-0 border-2 border-neutral-800/20" />
            <div className="relative">
              {imageError ? (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500">Image unavailable</span>
                </div>
              ) : (
                <img
                  src={imageUrl}
                  alt={cardData.title || 'Tarot Card'}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                  crossOrigin="anonymous"
                />
              )}
              {/* Image blend mode overlay */}
              <div className="absolute inset-0 mix-blend-color-burn opacity-20 bg-stone-100" />
              <div className="absolute inset-0 mix-blend-overlay opacity-10 bg-gradient-to-b from-neutral-900 to-transparent" />
            </div>
          </div>

          {/* Elemental symbol - now more visible */}
          {/* <div className="absolute top-1/4 right-8 w-12 h-12 rounded-full 
                        flex items-center justify-center transform -rotate-12
                        bg-stone-50/20 backdrop-blur-sm">
            <span className="text-xl font-serif text-neutral-800/50">{elementalSymbol}</span>
          </div> */}

          {/* Card title in vintage typography with reduced contrast */}
          <div className="text-center mb-4">
            <h2 className="font-serif text-2xl text-neutral-800/80 tracking-wide uppercase z-50">
              {cardData.title}
            </h2>
            <div className="mt-2 text-sm text-neutral-600/90 italic leading-relaxed">
              {cardData.description}
            </div>
          </div>

          {/* Keywords with vintage styling */}
          <div className="flex flex-wrap justify-center gap-2">
            {keywords.map((keyword, index) => (
              <span
                key={index}
                className="text-xs text-neutral-700 border border-neutral-400/30 
                         px-2 py-1 rounded-sm bg-neutral-100/50"
              >
                {keyword}
              </span>
            ))}
          </div>

          {/* Bottom decorative flourish */}
          <div className="w-full mt-4 h-6" />
        </div>

        {/* Corner decorations - moved inward and darker */}
        <div className="absolute top-6 left-6 w-12 h-12 border-l-2 border-t-2 border-neutral-800/60 z-10" />
        <div className="absolute top-6 right-6 w-12 h-12 border-r-2 border-t-2 border-neutral-800/60 z-10" />
        <div className="absolute bottom-6 left-6 w-12 h-12 border-l-2 border-b-2 border-neutral-800/60 z-10" />
        <div className="absolute bottom-6 right-6 w-12 h-12 border-r-2 border-b-2 border-neutral-800/60 z-10" />
      </div>
    </div>
  );
};

export default TarotCard;